// src/personel-takip/src/pages/BelgeYukleme.js
import React, { useEffect, useMemo, useState } from "react";
import { db, storage, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  deleteDoc as fsDeleteDoc,
  doc as fsDoc,
  query,
  where,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import Select from "react-select";

/* ========= Yardımcılar ========= */
const U = (s = "") => (s || "").trim().toUpperCase();
const toNameCase = (s = "") =>
  (s || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
const byLocale = (a, b) =>
  (a || "").localeCompare(b || "", "tr", { sensitivity: "base" });
const normalizeTurkish = (s = "") =>
  s
    .replace(/İ/g, "I").replace(/ı/g, "i")   // Türkçe'ye özgü — NFD'den önce
    .replace(/ğ/g, "g").replace(/Ğ/g, "G")
    .replace(/ü/g, "u").replace(/Ü/g, "U")
    .replace(/ş/g, "s").replace(/Ş/g, "S")
    .replace(/ö/g, "o").replace(/Ö/g, "O")
    .replace(/ç/g, "c").replace(/Ç/g, "C")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // kalan aksan işaretlerini at
const sanitizeFileName = (name = "") =>
  normalizeTurkish(name).replace(/[^a-zA-Z0-9._\- ]/g, "_");

const toDDMMYYYYDash = (val = "") => {
  const s = (val || "").trim();
  if (!s) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = /^(\d{1,2})[.\-\/](\d{1,2})[.\-\/](\d{4})$/.exec(s);
  if (dmy) {
    const d = dmy[1].padStart(2, "0");
    const m = dmy[2].padStart(2, "0");
    const y = dmy[3];
    return `${d}-${m}-${y}`;
  }
  const ymd = /^(\d{4})[.\-\/](\d{1,2})[.\-\/](\d{1,2})$/.exec(s);
  if (ymd) {
    const y = ymd[1];
    const m = ymd[2].padStart(2, "0");
    const d = ymd[3].padStart(2, "0");
    return `${d}-${m}-${y}`;
  }
  return s.replaceAll(".", "-").replaceAll("/", "-");
};
const addMonths = (ddmmyyyy, months = 12) => {
  const [d, m, y] = (ddmmyyyy || "").split("-").map((v) => parseInt(v, 10));
  if (!d || !m || !y) return ddmmyyyy || "";
  const dt = new Date(y, m - 1, d);
  const orig = dt.getDate();
  dt.setMonth(dt.getMonth() + months, 1);
  const last = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
  dt.setDate(Math.min(orig, last));
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yy = dt.getFullYear();
  return `${dd}-${mm}-${yy}`;
};
const diffDays = (fromDDMMYYYY, toDDMMYYYY) => {
  const p = (s) => {
    const [d, m, y] = (s || "").split("-").map((n) => parseInt(n, 10));
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const a = p(fromDDMMYYYY),
    b = p(toDDMMYYYY);
  return Math.ceil((b - a) / (1000 * 60 * 60 * 24));
};
const todayDDMMYYYY = () => {
  const t = new Date();
  const d = String(t.getDate()).padStart(2, "0");
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const y = t.getFullYear();
  return `${d}-${m}-${y}`;
};
const dateSortKey = (ddmmyyyy = "") => {
  const [d, m, y] = toDDMMYYYYDash(ddmmyyyy).split("-").map((n) => parseInt(n, 10));
  if (!d || !m || !y) return "";
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};
/** Excel (.xls) üretimi (kütüphanesiz) */
const exportRowsToExcel = (rows, headers, filename = "liste.xls") => {
  const esc = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const thead =
    "<tr>" + headers.map((h) => `<th>${esc(h)}</th>`).join("") + "</tr>";
  const tbody =
    rows
      .map((r) => "<tr>" + r.map((c) => `<td>${esc(c)}</td>`).join("") + "</tr>")
      .join("");
  const html =
    "<html><head><meta charset='utf-8'></head><body><table border='1'>" +
    thead +
    tbody +
    "</table></body></html>";
  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/* ---- Rol bazlı süre & uyarı helpers ---- */
const roleOf = (p) => U(p?.gorev || "");
const validityMonthsFor = (docType, roleUC) => {
  const base = parseInt(docType?.validityMonths, 10) || 0;
  const map = docType?.roleValidityMonths || {};
  const rb = parseInt(map?.[roleUC], 10);
  return Number.isFinite(rb) && rb > 0 ? rb : base;
};
const warnDayOf = (docType) => {
  if (Number.isFinite(docType?.warnDay)) return docType.warnDay;
  const arr = (docType?.warnDays || [])
    .map((n) => parseInt(n, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  return arr.length ? Math.min(...arr) : 7;
};

/* ================ Bileşen ================= */
const BelgeYukleme = () => {
  // Kaynak veriler
  const [personeller, setPersoneller] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [docs, setDocs] = useState([]);

  // Sol panel filtreleri
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [textSearch, setTextSearch] = useState("");

  // react-select çoklu seçimler
  const [selRoles, setSelRoles] = useState([]); // array of string (U)
  const [selSites, setSelSites] = useState([]); // array of string (U)
  const [selPersons, setSelPersons] = useState([]); // array of "Ad Soyad"
  const [selDocTypeIds, setSelDocTypeIds] = useState([]); // array of docType.id

  // Seçimler
  const [selectedPersonId, setSelectedPersonId] = useState("");
  const selectedPerson = useMemo(
    () => personeller.find((p) => p.id === selectedPersonId),
    [personeller, selectedPersonId]
  );

  // Yükleme formu
  const [selDocTypeId, setSelDocTypeId] = useState("");
  const [docDate, setDocDate] = useState(""); // YYYY-MM-DD
  const [docScopeName, setDocScopeName] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const selectedDocType = useMemo(
    () => docTypes.find((t) => t.id === selDocTypeId),
    [docTypes, selDocTypeId]
  );
  
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const multiSelectProps = {
    isMulti: true,
    closeMenuOnSelect: false,
    hideSelectedOptions: false,
  };

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalRows, setModalRows] = useState([]); // array of arrays
  const [modalHeaders, setModalHeaders] = useState([]);

  // ======= Yetkiye göre görülebilir tesisler (Puantaj mantığıyla aynı) =======
  const [allowedTesis, setAllowedTesis] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const emailLc = auth?.currentUser?.email?.toLowerCase();
        if (!emailLc) {
          setAllowedTesis([]);
          return;
        }
        const qP = query(
          collection(db, "personeller"),
          where("email", "==", emailLc)
        );
        const sP = await getDocs(qP);
        const own = new Set();
        if (!sP.empty) {
          sP.forEach((d) => {
            const data = d.data() || {};
            if (data.tesis) own.add(U(data.tesis));
            let extra = data.gorebilecegiTesisler;
            if (typeof extra === "string")
              extra = extra
                .split(",")
                .map((x) => x.trim())
                .filter(Boolean);
            (extra || []).forEach((t) => own.add(U(t)));
          });
        }
        setAllowedTesis(Array.from(own));
      } catch {
        setAllowedTesis([]);
      }
    })();
  }, [auth?.currentUser?.email]);

  // ======= Veri okuma =======
  useEffect(() => {
    (async () => {
      const [pSnap, tSnap, dSnap] = await Promise.all([
        getDocs(collection(db, "personeller")),
        getDocs(collection(db, "personelDocTypes")),
        getDocs(collection(db, "personelDocs")),
      ]);
      const P = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const T = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const D = dSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Sıralama (ham)
      P.sort(
        (a, b) =>
          byLocale(a.ad, b.ad) ||
          byLocale(a.soyad, b.soyad) ||
          byLocale(a.tesis, b.tesis)
      );
      T.sort((a, b) => byLocale(a.name, b.name));

      setPersoneller(P);
      setDocTypes(T);
      setDocs(D);
      setDataLoading(false);
    })();
  }, []);

  /* ---------- Aktif/Pasif ve görünür kümeler ---------- */
  const aktifMi = (tarih = {}) => {
    const g = Object.keys(tarih || {}).filter((k) => k.startsWith("giris"))
      .length;
    const c = Object.keys(tarih || {}).filter((k) => k.startsWith("cikis"))
      .length;
    return g > c;
  };

  const sortedPeriodEntries = (tarih = {}, prefix) =>
    Object.entries(tarih || {})
      .filter(([k, v]) => k.startsWith(prefix) && v)
      .map(([key, value]) => ({
        key,
        idx: parseInt(String(key).replace(prefix, ""), 10) || 0,
        value: toDDMMYYYYDash(value),
      }))
      .sort((a, b) => a.idx - b.idx);

  const currentEmploymentPeriod = (person) => {
    const entries = sortedPeriodEntries(person?.tarih || {}, "giris");
    return entries.length ? entries[entries.length - 1] : null;
  };

  const docSatisfiesTypeForPerson = (docItem, docType, person) => {
    if (!docItem || !docType || docItem.docTypeId !== docType.id) return false;
    if (!docType.renewOnRehire) return true;
    const period = currentEmploymentPeriod(person);
    if (!period) return false;
    return (
      docItem.employmentPeriodKey === period.key ||
      docItem.employmentStartDate === period.value ||
      (!docItem.employmentPeriodKey &&
        !docItem.employmentStartDate &&
        docItem.docDate &&
        dateSortKey(docItem.docDate) >= dateSortKey(period.value))
    );
  };

  // Görünebilir personeller: sadece allowedTesis içinde olanlar
  const visiblePersonsBase = useMemo(() => {
    const allow = new Set(allowedTesis);
    const arr = personeller.filter((p) => allow.has(U(p.tesis || "")));
    return arr;
  }, [personeller, allowedTesis]);

  const activePersonsRaw = useMemo(
    () => visiblePersonsBase.filter((p) => aktifMi(p.tarih || {})),
    [visiblePersonsBase]
  );

  // ==== Select seçeneklerini görünür kümeden türet
  const roleOptions = useMemo(() => {
    const s = new Set();
    visiblePersonsBase.forEach((p) => p.gorev && s.add(U(p.gorev)));
    return Array.from(s)
      .sort(byLocale)
      .map((v) => ({ value: v, label: v }));
  }, [visiblePersonsBase]);

  const siteOptionsAll = useMemo(() => {
    const s = new Set();
    visiblePersonsBase.forEach((p) => p.tesis && s.add(U(p.tesis)));
    return Array.from(s)
      .sort(byLocale)
      .map((v) => ({ value: v, label: v }));
  }, [visiblePersonsBase]);

  const siteOptionsActive = useMemo(() => {
    const s = new Set();
    activePersonsRaw.forEach((p) => p.tesis && s.add(U(p.tesis)));
    return Array.from(s)
      .sort(byLocale)
      .map((v) => ({ value: v, label: v }));
  }, [activePersonsRaw]);

  const personOptions = useMemo(() => {
    return visiblePersonsBase
      .map((p) => ({
        value: `${toNameCase(p.ad || "")} ${U(p.soyad || "")}`,
        label: `${toNameCase(p.ad || "")} ${U(p.soyad || "")}`,
        id: p.id,
      }))
      .sort((a, b) => byLocale(a.label, b.label));
  }, [visiblePersonsBase]);

  // ===== Belge tipi seçenek evrenini de kısıtla
  const visibleRolesSet = useMemo(() => {
    const s = new Set();
    visiblePersonsBase.forEach((p) => p.gorev && s.add(U(p.gorev)));
    return s;
  }, [visiblePersonsBase]);

  const visiblePersonIds = useMemo(
    () => new Set(visiblePersonsBase.map((p) => p.id)),
    [visiblePersonsBase]
  );

  const requiredTypeIdsForVisible = useMemo(() => {
    const ids = new Set();
    docTypes.forEach((t) => {
      const rs = (t.rolesRequired || []).map(U);
      for (const r of visibleRolesSet) {
        if (rs.includes(r)) {
          ids.add(t.id);
          break;
        }
      }
    });
    return ids;
  }, [docTypes, visibleRolesSet]);

  const uploadedTypeIdsForVisible = useMemo(() => {
    const ids = new Set();
    docs.forEach((d) => {
      if (visiblePersonIds.has(d.personelId)) ids.add(d.docTypeId);
    });
    return ids;
  }, [docs, visiblePersonIds]);

  const allowedDocTypeIdSet = useMemo(() => {
    const s = new Set(requiredTypeIdsForVisible);
    uploadedTypeIdsForVisible.forEach((id) => s.add(id));
    return s;
  }, [requiredTypeIdsForVisible, uploadedTypeIdsForVisible]);

  const docTypeOptions = useMemo(() => {
    return docTypes
      .filter((t) => allowedDocTypeIdSet.has(t.id))
      .sort((a, b) => byLocale(a.name, b.name))
      .map((t) => ({ value: t.id, label: t.name }));
  }, [docTypes, allowedDocTypeIdSet]);

  // Uygulanan filtreler → personel listesi
  const filteredActivePersons = useMemo(() => {
    const roleSet = new Set(selRoles);
    const siteSet = new Set(selSites);
    const personSet = new Set(selPersons);

    return activePersonsRaw
      .filter((p) => {
        if (roleSet.size && !roleSet.has(U(p.gorev || ""))) return false;
        if (siteSet.size && !siteSet.has(U(p.tesis || ""))) return false;
        if (personSet.size) {
          const nm = `${toNameCase(p.ad || "")} ${U(p.soyad || "")}`;
          if (!personSet.has(nm)) return false;
        }
        if (textSearch) {
          const s = textSearch.toLowerCase();
          const hay = `${p.ad} ${p.soyad} ${p.gorev} ${p.tesis}`.toLowerCase();
          if (!hay.includes(s)) return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          byLocale(a.ad, b.ad) ||
          byLocale(a.soyad, b.soyad) ||
          byLocale(a.tesis, b.tesis)
      );
  }, [activePersonsRaw, selRoles, selSites, selPersons, textSearch]);

  // Belgeleri kişilerle eşle
  const docsByPerson = useMemo(() => {
    const map = new Map();
    docs.forEach((d) => {
      if (!visiblePersonIds.has(d.personelId)) return; // sadece görülenlerin belgeleri
      if (!map.has(d.personelId)) map.set(d.personelId, []);
      map.get(d.personelId).push(d);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => byLocale(a.docTypeName, b.docTypeName));
    }
    return map;
  }, [docs, visiblePersonIds]);

  const requiredTypesForRole = (role) => {
    const R = U(role || "");
    return docTypes
      .filter((t) => (t.rolesRequired || []).map(U).includes(R))
      .filter((t) => allowedDocTypeIdSet.has(t.id)); // ekstra güvenlik
  };

  const today = todayDDMMYYYY();

  // Kişi özetleri (eksik/uyarı/toplam)
  const personSummaries = useMemo(() => {
    return filteredActivePersons.map((p) => {
      const list = docsByPerson.get(p.id) || [];
      const req = requiredTypesForRole(p.gorev);
      const missing = req.filter(
        (t) => !list.some((d) => docSatisfiesTypeForPerson(d, t, p))
      );

      const uyari = list.reduce((acc, d) => {
        if (!d.expireDate) return acc;
        const remaining = diffDays(today, d.expireDate);
        const t = docTypes.find((x) => x.id === d.docTypeId);
        const warn = t ? warnDayOf(t) : 7;
        if (Number.isFinite(remaining) && remaining <= warn) return acc + 1;
        return acc;
      }, 0);

      return {
        id: p.id,
        adSoyad: `${toNameCase(p.ad || "")} ${U(p.soyad || "")}`,
        gorev: U(p.gorev || ""),
        tesis: U(p.tesis || ""),
        toplam: list.length,
        eksik: missing.length,
        uyari,
        _missingTypes: missing,
      };
    });
  }, [filteredActivePersons, docsByPerson, docTypes, today]);

  // Seçili kişi görünür değilse seçim temizle
  useEffect(() => {
    if (!selectedPersonId) return;
    const ok = visiblePersonsBase.some((p) => p.id === selectedPersonId);
    if (!ok) setSelectedPersonId("");
  }, [visiblePersonsBase, selectedPersonId]);

  // Sağ panelde seçilen kişi
  const selectedDocs = useMemo(
    () => (selectedPerson ? docsByPerson.get(selectedPerson.id) || [] : []),
    [docsByPerson, selectedPerson]
  );

  const selectedMissingTypes = useMemo(() => {
    if (!selectedPerson) return [];
    return requiredTypesForRole(selectedPerson.gorev).filter(
      (t) => !selectedDocs.some((d) => docSatisfiesTypeForPerson(d, t, selectedPerson))
    );
  }, [selectedPerson, selectedDocs, docTypes]);

  // Sağ panel "Belge Tipi" seçenekleri: seçilen kişinin rolüne zorunlu ∪ o kişide mevcut
  const docTypeOptionsForSelected = useMemo(() => {
    if (!selectedPerson) {
      // Seçim yoksa yine de izinli tipleri dön (soldaki global select gibi)
      return docTypes
        .filter((t) => allowedDocTypeIdSet.has(t.id))
        .sort((a, b) => byLocale(a.name, b.name));
    }
    const roleUC = U(selectedPerson.gorev || "");
    const requiredForRole = new Set(
      docTypes
        .filter((t) => (t.rolesRequired || []).map(U).includes(roleUC))
        .map((t) => t.id)
    );
    const uploadedForPerson = new Set(
      (docsByPerson.get(selectedPerson.id) || []).map((d) => d.docTypeId)
    );
    const allowedForSelected = new Set();
    requiredForRole.forEach((id) => allowedForSelected.add(id));
    uploadedForPerson.forEach((id) => allowedForSelected.add(id));

    return docTypes
      .filter((t) => allowedForSelected.has(t.id))
      .sort((a, b) => {
        const aReq = (a.rolesRequired || [])
          .map(U)
          .includes(roleUC)
          ? 0
          : 1;
        const bReq = (b.rolesRequired || [])
          .map(U)
          .includes(roleUC)
          ? 0
          : 1;
        return aReq - bReq || byLocale(a.name, b.name);
      });
  }, [selectedPerson, docTypes, docsByPerson, allowedDocTypeIdSet]);

  // Seçenek daralınca kullanılmayan seçimleri temizle
  useEffect(() => {
    const valid = new Set(roleOptions.map((o) => o.value));
    setSelRoles((prev) => prev.filter((v) => valid.has(v)));
  }, [roleOptions]);

  useEffect(() => {
    const allSites = (
      siteOptionsActive.length ? siteOptionsActive : siteOptionsAll
    ).map((o) => o.value);
    const valid = new Set(allSites);
    setSelSites((prev) => prev.filter((v) => valid.has(v)));
  }, [siteOptionsActive, siteOptionsAll]);

  useEffect(() => {
    const valid = new Set(personOptions.map((o) => o.value));
    setSelPersons((prev) => prev.filter((v) => valid.has(v)));
  }, [personOptions]);

  useEffect(() => {
    const valid = new Set(docTypeOptions.map((o) => o.value));
    setSelDocTypeIds((prev) => prev.filter((v) => valid.has(v)));
  }, [docTypeOptions]);

  useEffect(() => {
    if (!selDocTypeId) return;
    const ok = docTypeOptionsForSelected.some((t) => t.id === selDocTypeId);
    if (!ok) setSelDocTypeId("");
  }, [docTypeOptionsForSelected, selDocTypeId]);

  useEffect(() => {
    if (!selectedDocType?.allowMultiple) setDocScopeName("");
  }, [selectedDocType]);

  /* ---------- Filtreye uyan belgeler + metrikler ---------- */
  const filteredDocTypeIdSet = useMemo(
    () => new Set(selDocTypeIds),
    [selDocTypeIds]
  );
  const filteredPersonIdSet = useMemo(
    () => new Set(filteredActivePersons.map((p) => p.id)),
    [filteredActivePersons]
  );

  // Balonlar ve ZIP için belge evreni (sadece görülebilen kişiler)
  const filteredDocsUniverse = useMemo(() => {
    const arr = docs
      .filter((d) => visiblePersonIds.has(d.personelId))
      .filter((d) => filteredPersonIdSet.has(d.personelId))
      .filter((d) =>
        filteredDocTypeIdSet.size ? filteredDocTypeIdSet.has(d.docTypeId) : true
      )
      .map((d) => {
        const t = docTypes.find((x) => x.id === d.docTypeId);
        const remaining = d.expireDate ? diffDays(today, d.expireDate) : null;
        const warn = t ? warnDayOf(t) : 7;
        return {
          ...d,
          remainingDays: remaining,
          warnDay: warn,
        };
      });
    return arr;
  }, [
    docs,
    visiblePersonIds,
    filteredPersonIdSet,
    filteredDocTypeIdSet,
    docTypes,
    today,
  ]);

  const toplamEvrak = filteredDocsUniverse.length;

  const toplamEksik = useMemo(() => {
    return personSummaries.reduce((acc, x) => acc + x.eksik, 0);
  }, [personSummaries]);

  // Geçersiz = süresi dolmuş (remainingDays < 0)
  const gecersizEvrakSayisi = useMemo(() => {
    return filteredDocsUniverse.filter(
      (d) => Number.isFinite(d.remainingDays) && d.remainingDays < 0
    ).length;
  }, [filteredDocsUniverse]);

  // Uyarı = dolmamış ama eşiğe yaklaşmış (0 <= remainingDays <= warnDay)
  const uyaridakiEvrakSayisi = useMemo(() => {
    return filteredDocsUniverse.filter(
      (d) =>
        Number.isFinite(d.remainingDays) &&
        Number.isFinite(d.warnDay) &&
        d.remainingDays >= 0 &&
        d.remainingDays <= d.warnDay
    ).length;
  }, [filteredDocsUniverse]);

  /* ---------- Modal işlemleri ---------- */
  const openModal = (title, headers, rows) => {
    setModalTitle(title);
    setModalHeaders(headers);
    setModalRows(rows);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  const openToplamEvrakModal = () => {
    const headers = [
      "Personel",
      "Tesis",
      "Görev",
      "Belge",
      "Belge Tarihi",
      "Bitiş",
      "Kalan Gün",
    ];
    const rows = filteredDocsUniverse.map((d) => [
      d.personelAdSoyad || "",
      U(d.tesis || ""),
      U(d.gorev || ""),
      d.docTypeName || "",
      d.docDate || "",
      d.expireDate || "",
      d.remainingDays ?? "—",
      d.fileUrl || "",
    ]);
    openModal("Toplam Evrak", headers, rows);
  };

  const openEksikEvrakModal = () => {
    const headers = ["Personel", "Tesis", "Görev", "Eksik Belgeler"];
    const rows = personSummaries
      .filter((x) => x.eksik > 0)
      .map((x) => [
        x.adSoyad,
        x.tesis,
        x.gorev,
        x._missingTypes.map((m) => m.name).join(", "),
      ]);
    openModal("Eksik Evraklar", headers, rows);
  };

  const openGecersizModal = () => {
    const headers = [
      "Personel", "Tesis", "Görev", "Belge", "Belge Tarihi", "Bitiş", "Kalan Gün",
    ];
    const rows = filteredDocsUniverse
      .filter((d) => Number.isFinite(d.remainingDays) && d.remainingDays < 0)
      .map((d) => [
        d.personelAdSoyad || "",
        U(d.tesis || ""),
        U(d.gorev || ""),
        d.docTypeName || "",
        d.docDate || "",
        d.expireDate || "",
        d.remainingDays ?? "—",
        d.fileUrl || "",
      ]);
    openModal("Geçersiz Evraklar", headers, rows);
  };

  const openUyariModal = () => {
    const headers = [
      "Personel", "Tesis", "Görev", "Belge", "Belge Tarihi", "Bitiş", "Kalan Gün",
    ];
    const rows = filteredDocsUniverse
      .filter(
        (d) =>
          Number.isFinite(d.remainingDays) &&
          Number.isFinite(d.warnDay) &&
          d.remainingDays >= 0 &&
          d.remainingDays <= d.warnDay
      )
      .map((d) => [
        d.personelAdSoyad || "",
        U(d.tesis || ""),
        U(d.gorev || ""),
        d.docTypeName || "",
        d.docDate || "",
        d.expireDate || "",
        d.remainingDays ?? "—",
        d.fileUrl || "",
      ]);
    openModal("Uyarı Süresinde Olanlar", headers, rows);
  };

  const exportModalToExcel = () => {
    if (!modalRows.length) return;
    exportRowsToExcel(
      modalRows.map((r) => r.slice(0, modalHeaders.length)),
      modalHeaders,
      "liste.xls"
    );
  };

  /* ---------- İndir/Sil yardımcıları ---------- */
  const downloadOne = async (d) => {
    if (!d?.fileUrl) return;
    try {
      const [{ saveAs }] = await Promise.all([
        import("file-saver").catch(() => ({ saveAs: null })),
      ]);
      if (!saveAs) {
        window.open(d.fileUrl, "_blank", "noopener,noreferrer");
        return;
      }
      const resp = await fetch(d.fileUrl, { mode: "cors" });
      const blob = await resp.blob();
      const name = sanitizeFileName(
        `${d.personelAdSoyad || "Personel"} - ${d.docTypeName || "Belge"}.pdf`
      );
      saveAs(blob, name);
    } catch (e) {
      console.error("indirme hatası", e);
      window.open(d.fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const deleteOne = async (d) => {
    if (!d?.id) return;
    const ok = window.confirm(
      `"${d.docTypeName}" belgesini silmek istiyor musun?`
    );
    if (!ok) return;
    try {
      // Storage
      if (d.storagePath) {
        try {
          await deleteObject(ref(storage, d.storagePath));
        } catch (e) {
          console.warn("Storage silinemedi (devam ediliyor):", e?.message);
        }
      }
      // Firestore
      await fsDeleteDoc(fsDoc(db, "personelDocs", d.id));
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
      alert("Belge silindi ✅");
    } catch (e) {
      console.error("Silme hatası", e);
      alert("Belge silinemedi.");
    }
  };

  const downloadAllForPerson = async (personId) => {
    const items = (docsByPerson.get(personId) || []).filter((d) => !!d.fileUrl);
    if (!items.length) return alert("Bu personele ait indirilebilir belge yok.");
    try {
      const [{ default: JSZip }, { saveAs }] = await Promise.all([
        import("jszip").catch(() => ({ default: null })),
        import("file-saver").catch(() => ({ saveAs: null })),
      ]);
      if (!JSZip || !saveAs) {
        for (const d of items) {
          const a = document.createElement("a");
          a.href = d.fileUrl;
          a.download = sanitizeFileName(
            `${d.personelAdSoyad || "Personel"} - ${
              d.docTypeName || "Belge"
            }.pdf`
          );
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(
        sanitizeFileName(items[0]?.personelAdSoyad || "personel")
      );
      await Promise.all(
        items.map(async (d) => {
          const resp = await fetch(d.fileUrl, { mode: "cors" });
          const blob = await resp.blob();
          folder.file(
            sanitizeFileName(`${d.docTypeName || "Belge"}.pdf`),
            blob
          );
        })
      );
      const content = await zip.generateAsync({ type: "blob" });
      const zipName = sanitizeFileName(
        `${items[0]?.personelAdSoyad || "personel"} - belgeler.zip`
      );
      saveAs(content, zipName);
    } catch (e) {
      console.error("ZIP (kişisel) hatası:", e);
      alert("ZIP oluşturulamadı.");
    }
  };

  /* ---------- ZIP indirme (filtreye uyan yüklü belgeler) ---------- */
  const downloadFilteredAsZip = async () => {
    const items = filteredDocsUniverse.filter((d) => !!d.fileUrl);
    if (!items.length) return alert("İndirilecek uygun belge bulunamadı.");

    const buildName = (d) => {
      const nm = `${d.personelAdSoyad || "Personel"} - ${
        d.docTypeName || "Belge"
      }.pdf`;
      return sanitizeFileName(nm);
    };

    try {
      const [{ default: JSZip }, { saveAs }] = await Promise.all([
        import("jszip").catch(() => ({ default: null })),
        import("file-saver").catch(() => ({ saveAs: null })),
      ]);

      if (!JSZip || !saveAs) {
        for (const d of items) {
          const a = document.createElement("a");
          a.href = d.fileUrl;
          a.download = buildName(d);
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        alert("JSZip bulunamadı: dosyalar tek tek indirildi.");
        return;
      }

      const zip = new JSZip();
      const folder = zip.folder("belgeler");
      await Promise.all(
        items.map(async (d) => {
          const resp = await fetch(d.fileUrl, { mode: "cors" });
          const blob = await resp.blob();
          folder.file(buildName(d), blob);
        })
      );
      const content = await zip.generateAsync({ type: "blob" });
      import("file-saver").then(({ saveAs }) =>
        saveAs(content, "filtreli-belgeler.zip")
      );
    } catch (e) {
      console.error("ZIP hatası:", e);
      alert("ZIP oluşturulamadı. Konsolu kontrol edin.");
    }
  };

  /* ---------- Yükleme ---------- */
  const startUploadForType = (docTypeId) => {
    setSelDocTypeId(docTypeId);
    const el = document.getElementById("upload-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const uploadDoc = async () => {
    if (!selectedPersonId || !selDocTypeId)
      return alert("Lütfen personel ve belge tipi seçin.");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(docDate))
      return alert("Lütfen belge tarihini seçin.");
    if (!file) return alert("Lütfen PDF dosyası seçin.");
    if (file.type && !/pdf/i.test(file.type))
      return alert("Sadece PDF yükleyebilirsiniz.");

    try {
      setLoadingUpload(true);
      setUploadPct(0);

      const p = selectedPerson;
      const t = docTypes.find((x) => x.id === selDocTypeId);
      if (!p || !t) return;
      const employmentPeriod = currentEmploymentPeriod(p);

      const dd = toDDMMYYYYDash(docDate);
      const months = validityMonthsFor(t, roleOf(p)); // ROL BAZLI AY
      const expire = addMonths(dd, months || 0);

      const safe = sanitizeFileName(
        `${toNameCase(p.ad || "")} ${U(p.soyad || "")} - ${U(t.name)}.pdf`
      );

      // 🔄 İSTENEN YENİ KLASÖR YAPISI:
      // personel-belgeleri/mesleki-belgeler/{personelId}/{timestamp}-Ad_Soyad_-_BELGE.pdf
      const path = `personel-belgeleri/mesleki-belgeler/${selectedPersonId}/${Date.now()}-${safe}`;

      const sref = ref(storage, path);

      // ---- Progress bar için resumable upload
      const task = uploadBytesResumable(sref, file);
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct = Math.round(
              (snap.bytesTransferred / snap.totalBytes) * 100
            );
            setUploadPct(pct);
          },
          (err) => reject(err),
          () => resolve()
        );
      });

      const fileUrl = await getDownloadURL(sref);

      const payload = {
        personelId: selectedPersonId,
        personelAdSoyad: `${toNameCase(p.ad || "")} ${U(p.soyad || "")}`,
        gorev: U(p.gorev || ""),
        tesis: U(p.tesis || ""),
        docTypeId: selDocTypeId,
        docTypeName: t.name,
        docDate: dd,
        expireDate: expire,
        employmentPeriodKey: employmentPeriod?.key || "",
        employmentStartDate: employmentPeriod?.value || "",
        scopeName: t.allowMultiple ? docScopeName.trim() : "",
        fileUrl,
        storagePath: path,
        createdBy: auth?.currentUser?.email || "anonim",
        createdAt: serverTimestamp(),
      };

      const refDoc = await addDoc(collection(db, "personelDocs"), payload);
      setDocs((prev) => [...prev, { id: refDoc.id, ...payload }]);

      setSelDocTypeId("");
      setDocDate("");
      setDocScopeName("");
      setFile(null);
      alert("Belge yüklendi ✅");
    } catch (err) {
      console.error("Yükleme hatası:", err);
      alert("Yükleme başarısız oldu. Konsolu kontrol edin.");
    } finally {
      setLoadingUpload(false);
      setUploadPct(0);
    }
  };

  /* ---------- Stil ve UI ---------- */
  return (
    <div className="page">
      <style>{`
        .page{ padding:14px; font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial; background:#fff; }
        .layout{ display:grid; grid-template-columns: 340px 1fr; gap:12px; }
        @media (max-width: 980px){ .layout{ grid-template-columns: 1fr; } }

        .panel{ border:1px solid #e5e7eb; border-radius:10px; background:#fff; }
        .panel .head{ padding:10px 12px; border-bottom:1px solid #e5e7eb; font-weight:700; display:flex; align-items:center; justify-content:space-between; gap:8px; }
        .panel .body{ padding:10px 12px; }

        .row{ display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        input[type="text"], input[type="date"], select {
          padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; box-sizing:border-box;
        }
        input[type="file"]{ font-size:13px; }
        .btn{ padding:8px 12px; border-radius:8px; border:1px solid #d1d5db; background:#111827; color:#fff; font-weight:600; cursor:pointer; }
        .btn.light{ background:#fff; color:#111827; }
        .btn.ghost{ background:#fff; border-color:#e5e7eb; }
        .btn.red{ background:#991b1b; border-color:#991b1b; }
        .btn:disabled{ opacity:.6; cursor:not-allowed; }
        .muted{ color:#6b7280; font-size:12px; }

        .chip{ padding:2px 8px; border:1px solid #e5e7eb; background:#f1f5f9; border-radius:999px; font-size:12px; cursor:pointer; }
        .chip.bad{ background:#fee2e2; border-color:#fecaca; color:#991b1b; }
        .chip.info{ background:#eef2ff; border-color:#c7d2fe; color:#1f2937; }
        .chip.info:hover{ filter:brightness(.95); }

        .list{ max-height: calc(100vh - 300px); overflow:auto; }
        .person{ display:flex; align-items:center; justify-content:space-between; gap:8px; padding:10px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:8px; cursor:pointer; }
        .person:hover{ background:#f8fafc; }
        .person.active{ background:#eef2ff; border-color:#c7d2fe; }
        .counts{ display:flex; gap:8px; align-items:center; }
        .leftTitle{ font-weight:600; margin-bottom:4px; }

        table{ width:100%; border-collapse:collapse; font-size:13px; }
        th, td{ border:1px solid #e5e7eb; padding:8px; text-align:left; vertical-align:top; }
        thead th{ background:#f8fafc; }

        .modal-back{ position:fixed; inset:0; background:rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:100; }
        .modal{ width:min(1000px, 94vw); height:min(80vh, 720px); background:#fff; border-radius:10px; overflow:hidden; display:flex; flex-direction:column; }
        .modal .mhead{ padding:8px 12px; border-bottom:1px solid #e5e7eb; display:flex; align-items:center; justify-content:space-between; }
        .modal .mbody{ flex:1; padding:8px 12px; overflow:auto; }

        .kpi{ display:flex; gap:10px; flex-wrap:wrap; margin-bottom:12px; }

        /* progress bar */
        .progress{ width:100%; height:8px; border-radius:999px; background:#e5e7eb; overflow:hidden; }
        .progress > div{ height:100%; background:#2563eb; }
        @keyframes _spin { to { transform: rotate(360deg); } }
      `}</style>

      <h2>Belge Yükleme</h2>

      {/* ÜST BİLGİ BALONLARI */}
      <div className="kpi">
        <span className="chip info" onClick={openToplamEvrakModal} title="Tıklayınca liste açılır">
          📄 Toplam Evrak: {toplamEvrak}
        </span>
        <span className="chip info" onClick={openEksikEvrakModal} title="Tıklayınca liste açılır">
          ⚠️ Eksik Evrak: {toplamEksik}
        </span>
        <span
          className="chip info"
          onClick={() => !dataLoading && openGecersizModal()}
          title="Tıklayınca liste açılır"
          style={{ color: gecersizEvrakSayisi > 0 ? '#b91c1c' : undefined, borderColor: gecersizEvrakSayisi > 0 ? '#fecaca' : undefined, background: gecersizEvrakSayisi > 0 ? '#fff5f5' : undefined }}
        >
          {dataLoading
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>🔴 Geçersiz <span style={{ display:'inline-block', width:12, height:12, border:'2px solid #ccc', borderTopColor:'#666', borderRadius:'50%', animation:'_spin 0.7s linear infinite' }} /></span>
            : `🔴 Geçersiz Evrak: ${gecersizEvrakSayisi}`}
        </span>
        <span
          className="chip info"
          onClick={() => !dataLoading && openUyariModal()}
          title="Tıklayınca liste açılır"
        >
          {dataLoading
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>⏰ Uyarı <span style={{ display:'inline-block', width:12, height:12, border:'2px solid #ccc', borderTopColor:'#666', borderRadius:'50%', animation:'_spin 0.7s linear infinite' }} /></span>
            : `⏰ Uyarı Süresinde Olanlar: ${uyaridakiEvrakSayisi}`}
        </span>
      </div>

      <div className="layout">
        {/* SOL PANEL */}
        <div className="panel">
          <div className="head">Personel Listesi</div>
          <div className="body">
            <div className="row" style={{ marginBottom: 10 }}>
              <input
                type="text"
                placeholder="Ara (ad / görev / tesis)"
                value={textSearch}
                onChange={(e) => setTextSearch(e.target.value)}
                style={{ flex: "1 1 160px" }}
              />
              <label className="row" style={{ gap: 6, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={onlyMissing}
                  onChange={(e) => setOnlyMissing(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                <span className="muted">Sadece eksik evrağı olanlar</span>
              </label>
            </div>

            {/* React-Select çoklu kutular */}
            <div
              className="row"
              style={{ alignItems: "stretch", marginBottom: 8 }}
            >
              <div style={{ flex: "1 1 100%", minWidth: 260 }}>
                <div className="muted">Görev (çoklu)</div>
                <Select
                  {...multiSelectProps}
                  options={roleOptions}
                  value={roleOptions.filter((o) => selRoles.includes(o.value))}
                  onChange={(vals) =>
                    setSelRoles((vals || []).map((v) => v.value))
                  }
                  placeholder="Görev seç..."
                />
              </div>
              <div style={{ flex: "1 1 100%", minWidth: 260 }}>
                <div className="muted">Tesis (çoklu)</div>
                <Select
                  {...multiSelectProps}
                  options={
                    siteOptionsActive.length ? siteOptionsActive : siteOptionsAll
                  }
                  value={(
                    siteOptionsActive.length ? siteOptionsActive : siteOptionsAll
                  ).filter((o) => selSites.includes(o.value))}
                  onChange={(vals) =>
                    setSelSites((vals || []).map((v) => v.value))
                  }
                  placeholder="Tesis seç..."
                />
              </div>
            </div>

            <div
              className="row"
              style={{ alignItems: "stretch", marginBottom: 8 }}
            >
              <div style={{ flex: "1 1 100%", minWidth: 260 }}>
                <div className="muted">Personel (çoklu)</div>
                <Select
                  {...multiSelectProps}
                  options={personOptions}
                  value={personOptions.filter((o) => selPersons.includes(o.value))}
                  onChange={(vals) =>
                    setSelPersons((vals || []).map((v) => v.value))
                  }
                  placeholder="Personel seç..."
                />
              </div>
              <div style={{ flex: "1 1 100%", minWidth: 260 }}>
                <div className="muted">Belge Tipi (çoklu)</div>
                <Select
                  {...multiSelectProps}
                  options={docTypeOptions}
                  value={docTypeOptions.filter((o) =>
                    selDocTypeIds.includes(o.value)
                  )}
                  onChange={(vals) =>
                    setSelDocTypeIds((vals || []).map((v) => v.value))
                  }
                  placeholder="Belge tipi seç..."
                />
              </div>
            </div>

            <div
              className="row"
              style={{ justifyContent: "flex-end", marginBottom: 8 }}
            >
              <button
                className="btn light"
                onClick={downloadFilteredAsZip}
                title="Filtreye uyan yüklü belgeleri ZIP indir"
              >
                ⬇️ Filtreye Uyan Belgeleri ZIP İndir
              </button>
            </div>

            <div className="list" style={{ marginTop: 10 }}>
              {personSummaries
                .filter((x) => !onlyMissing || x.eksik > 0)
                .map((p) => (
                  <div
                    key={p.id}
                    className={`person ${
                      p.id === selectedPersonId ? "active" : ""
                    }`}
                    onClick={() => setSelectedPersonId(p.id)}
                    title="Detayı görmek için tıkla"
                  >
                    <div>
                      <div className="leftTitle">{p.adSoyad}</div>
                      <div className="muted">
                        {p.gorev}
                        {p.tesis ? ` — ${p.tesis}` : ""}
                      </div>
                    </div>
                    <div className="counts">
                      <span className={`chip ${p.eksik > 0 ? "bad" : ""}`}>
                        Eksik: {p.eksik}
                      </span>
                      <span className="chip">Uyarı: {p.uyari}</span>
                      <span className="chip">Toplam: {p.toplam}</span>
                    </div>
                  </div>
                ))}
              {!personSummaries.filter((x) => !onlyMissing || x.eksik > 0)
                .length && <div className="muted">(Kayıt yok)</div>}
            </div>
          </div>
        </div>

        {/* SAĞ PANEL */}
        <div className="panel">
          <div className="head">
            <div>
              {selectedPerson
                ? `${toNameCase(selectedPerson.ad || "")} ${U(
                    selectedPerson.soyad || ""
                  )} — ${U(selectedPerson.gorev || "")}${
                    selectedPerson.tesis ? " — " + U(selectedPerson.tesis) : ""
                  }`
                : "Detay"}
            </div>
            {selectedPerson && (
              <button
                className="btn light"
                onClick={() => downloadAllForPerson(selectedPerson.id)}
                title="Bu personele ait tüm evrakları ZIP olarak indir"
              >
                ⬇️ Tüm Evraklarını İndir (ZIP)
              </button>
            )}
          </div>
          <div className="body">
            {!selectedPerson && (
              <div className="muted">
                Lütfen sol listeden bir personel seçin.
              </div>
            )}

            {selectedPerson && (
              <>
                {/* Eksik Belgeler */}
                <div style={{ marginBottom: 12 }}>
                  <div className="leftTitle">Eksik Belgeler</div>
                  {selectedMissingTypes.length ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Belge</th>
                          <th>Zorunlu</th>
                          <th>Geçerlilik (Ay)</th>
                          <th>İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedMissingTypes.map((t) => (
                          <tr key={t.id}>
                            <td>{t.name}</td>
                            <td>Evet</td>
                            <td>
                              {validityMonthsFor(
                                t,
                                roleOf(selectedPerson)
                              ) || "—"}
                            </td>
                            <td>
                              <button
                                className="btn light"
                                onClick={() => startUploadForType(t.id)}
                              >
                                Yükle
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="muted">(Eksik belge yok)</div>
                  )}
                </div>

                {/* Yüklü Belgeler */}
                <div style={{ marginBottom: 12 }}>
                  <div className="leftTitle">Yüklü Belgeler</div>
                  {selectedDocs.length ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Belge</th>
                          <th>Açıklama</th>
                          <th>Giriş Dönemi</th>
                          <th>Belge Tarihi</th>
                          <th>Bitiş</th>
                          <th>Kalan Gün</th>
                          <th>Dosya</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDocs.map((d) => {
                          const t = docTypes.find((x) => x.id === d.docTypeId);
                          const warn = t ? warnDayOf(t) : 7;
                          const remaining =
                            d.expireDate != null
                              ? diffDays(today, d.expireDate)
                              : null;
                          return (
                            <tr key={d.id}>
                              <td>{d.docTypeName}</td>
                              <td>{d.scopeName || "—"}</td>
                              <td>{d.employmentStartDate || "—"}</td>
                              <td>{d.docDate || "—"}</td>
                              <td>{d.expireDate || "—"}</td>
                              <td
                                style={{
                                  color:
                                    Number.isFinite(remaining) &&
                                    remaining <= warn
                                      ? "#b91c1c"
                                      : undefined,
                                }}
                              >
                                {remaining == null ? "—" : `${remaining} gün`}
                              </td>
                              <td>
                                {d.fileUrl ? (
                                  <a
                                    className="btn ghost"
                                    style={{
                                      textDecoration: "none",
                                      cursor: "pointer",
                                    }}
                                    href={d.fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Aç
                                  </a>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td style={{ display: "flex", gap: 6 }}>
                                <button
                                  className="btn light"
                                  onClick={() => downloadOne(d)}
                                >
                                  İndir
                                </button>
                                <button
                                  className="btn red"
                                  onClick={() => deleteOne(d)}
                                >
                                  Sil
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="muted">(Belge yüklenmemiş)</div>
                  )}
                </div>

                {/* Yükleme Formu */}
                <div
                  id="upload-section"
                  className="panel"
                  style={{ border: "1px dashed #cbd5e1", background: "#f8fafc" }}
                >
                  <div className="head">Belge Yükle</div>
                  <div className="body">
                    <div className="row" style={{ marginBottom: 8 }}>
                      <div style={{ minWidth: 240, flex: "1 1 220px" }}>
                        <div className="muted">Belge Tipi</div>
                        <select
                          value={selDocTypeId}
                          onChange={(e) => setSelDocTypeId(e.target.value)}
                          style={{ width: "100%" }}
                        >
                          <option value="">(Seçin)</option>
                          {docTypeOptionsForSelected.map((t) => {
                            const isReq = selectedPerson
                              ? (t.rolesRequired || [])
                                  .map(U)
                                  .includes(U(selectedPerson.gorev || ""))
                              : false;
                            return (
                              <option key={t.id} value={t.id}>
                                {t.name}
                                {isReq ? " — [ZORUNLU]" : ""}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div style={{ minWidth: 160 }}>
                        <div className="muted">Belge Tarihi</div>
                        <input
                          type="date"
                          value={
                            /^\d{4}-\d{2}-\d{2}$/.test(docDate) ? docDate : docDate
                          }
                          onChange={(e) => setDocDate(e.target.value)}
                          placeholder="YYYY-MM-DD"
                          style={{ width: "100%" }}
                        />
                      </div>

                      {selectedDocType?.allowMultiple && (
                        <div style={{ minWidth: 190 }}>
                          <div className="muted">{selectedDocType.multipleScopeLabel || "Bölge/Tesis"}</div>
                          <input
                            value={docScopeName}
                            onChange={(e) => setDocScopeName(e.target.value)}
                            placeholder={selectedDocType.multipleScopeLabel || "Bölge/Tesis"}
                            style={{ width: "100%" }}
                          />
                        </div>
                      )}

                      <div style={{ minWidth: 260, flex: "1 1 200px" }}>
                        <div className="muted">PDF Dosyası</div>
<input
  type="file"
  accept="application/pdf"
  onChange={(e) => {
    const selectedFile = e.target.files?.[0];
    setFile(selectedFile || null);
    if (selectedFile) {
      // Dosya için geçici bir ön izleme linki oluşturur
      setPreviewUrl(URL.createObjectURL(selectedFile));
    } else {
      setPreviewUrl(null);
    }
  }}
  style={{ width: "100%" }}
/>
                      </div>
                    </div>

                    <div className="row" style={{ alignItems: "center", width: "100%" }}>
                      <button className="btn" onClick={uploadDoc} disabled={loadingUpload}>
                        {loadingUpload ? "Yükleniyor..." : "Yükle"}
                      </button>
                      {loadingUpload && (
                        <div style={{ flex: 1, minWidth: 200 }}>


                          <div className="progress" title={`${uploadPct}%`}>
                            <div style={{ width: `${uploadPct}%` }} />
                          </div>
                          <div className="muted" style={{ marginTop: 4 }}>
                            Yükleme: %{uploadPct}
                          </div>
                        </div>
                      )}
                      {selDocTypeId &&
                        (() => {
                          const t = docTypes.find((x) => x.id === selDocTypeId);
                          if (!t || !selectedPerson) return null;
                          const months = validityMonthsFor(
                            t,
                            roleOf(selectedPerson)
                          );
                          const warn = warnDayOf(t);
                          return (
                            <span className="muted">
                              Seçili tip: <b>{t.name}</b>{" "}
                              {months ? `(Geçerlilik: ${months} ay)` : ""} — Uyarı:{" "}
                              {warn} gün kala
                            </span>
                          );
                        })()}
{/* ÖN İZLEME PANELİ BURADA BAŞLIYOR */}
{previewUrl && (
  <div style={{ marginTop: "10px", marginBottom: "10px", border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden", width: "100%" }}>
    <div className="muted" style={{ padding: "5px", background: "#f1f5f9", fontWeight: "bold" }}>Seçilen Dosya Ön İzleme:</div>
    <embed src={previewUrl} type="application/pdf" width="100%" height="400px" />
  </div>
)}

                    </div>
                  </div>
                </div>
              </>
)}
          </div> {/* .body bitti */}
        </div> {/* .panel bitti (Sağ Panel) */}
      </div> {/* .layout bitti */}


      {/* Modal */}
      {modalOpen && (
        <div className="modal-back" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="mhead">
              <div style={{ fontWeight: 700 }}>{modalTitle}</div>
              <div className="row">
                <button
                  className="btn light"
                  onClick={exportModalToExcel}
                  title="Excel indir"
                >
                  ⬇️ Excel
                </button>
                <button className="btn" onClick={closeModal} title="Kapat">
                  Kapat
                </button>
              </div>
            </div>
            <div className="mbody">
              {modalRows.length ? (
                <table>
                  <thead>
                    <tr>
                      {modalHeaders.map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                      <th>Dosya</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalRows.map((r, i) => {
                      const url = r[modalHeaders.length] || "";
                      return (
                        <tr key={i}>
                          {r.slice(0, modalHeaders.length).map((c, j) => (
                            <td key={j}>{c}</td>
                          ))}
                          <td>
                            {url ? (
                              <a
                                className="btn ghost"
                                style={{
                                  textDecoration: "none",
                                  cursor: "pointer",
                                }}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Aç
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="muted">(Kayıt yok)</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BelgeYukleme;
